import React, { useState } from 'react';

function Profile({ user, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user.name || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [payoutCard, setPayoutCard] = useState(user.payout_card || '');
  const [payoutPhone, setPayoutPhone] = useState(user.payout_phone || '');
  const [payoutMethod, setPayoutMethod] = useState(user.payout_method || 'card');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('token')
        },
        body: JSON.stringify({ 
          name, 
          phone, 
          payout_card: payoutCard,
          payout_phone: payoutPhone,
          payout_method: payoutMethod
        })
      });
      
      if (response.ok) {
        const updatedUser = await response.json();
        localStorage.setItem('user', JSON.stringify(updatedUser));
        onUpdate(updatedUser);
        setIsEditing(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (error) {
      console.error('Ошибка:', error);
      alert('Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  const maskCard = (card) => {
    if (!card) return '';
    const cleaned = card.replace(/\s/g, '');
    if (cleaned.length < 4) return card;
    return '**** **** **** ' + cleaned.slice(-4);
  };

  const maskPhone = (phone) => {
    if (!phone) return '';
    if (phone.length < 4) return phone;
    return '+7 *** *** ' + phone.slice(-2) + ' ' + phone.slice(-2);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        {/* Заголовок */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center text-2xl font-black text-industrial-bg">
            {(user.name || user.email || '?')[0].toUpperCase()}
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-black text-slate-900 m-0 uppercase tracking-tight">Профиль</h2>
            <p className="text-sm text-slate-500 m-0">Управление личными данными</p>
          </div>
          {saved && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1.5 rounded-lg text-sm font-medium">
              ✅ Сохранено
            </div>
          )}
        </div>

        {/* Личные данные */}
        <div className="mb-8">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            👤 Личные данные
          </h3>
          
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Email</label>
              <div className="px-4 py-3 bg-slate-50 rounded-xl text-slate-700 font-medium">
                {user.email}
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Имя</label>
              {isEditing ? (
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
              ) : (
                <div className="px-4 py-3 bg-slate-50 rounded-xl text-slate-700 font-medium">
                  {user.name || '—'}
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Телефон</label>
              {isEditing ? (
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+7XXXXXXXXXX" />
              ) : (
                <div className="px-4 py-3 bg-slate-50 rounded-xl text-slate-700 font-medium">
                  {user.phone || '—'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Реквизиты для выплат */}
        <div className="mb-8 pt-8 border-t border-slate-200">
            <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
              💳 Реквизиты для выплат
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Заполните, если планируете сдавать технику. Если вы только заказываете — можно пропустить.
            </p>

            {/* Номер карты — всегда видно */}
            <div className="mb-5">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                💳 Номер карты
              </label>
              {isEditing ? (
                <>
                  <input 
                    type="text" 
                    value={payoutCard} 
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 16);
                      const formatted = val.replace(/(\d{4})(?=\d)/g, '$1 ');
                      setPayoutCard(formatted);
                    }}
                    placeholder="0000 0000 0000 0000"
                    maxLength="19"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    💡 Карта должна быть оформлена на ваше имя
                  </p>
                </>
              ) : (
                <div className="px-4 py-3 bg-slate-50 rounded-xl text-slate-700 font-medium font-mono">
                  {user.payout_card ? maskCard(user.payout_card) : '— не указана —'}
                </div>
              )}
            </div>

            {/* Телефон для СБП — всегда видно */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                📱 Телефон для СБП
              </label>
              {isEditing ? (
                <>
                  <input 
                    type="tel" 
                    value={payoutPhone} 
                    onChange={(e) => setPayoutPhone(e.target.value)}
                    placeholder="+7XXXXXXXXXX"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    💡 По этому номеру можно получить деньги через СБП
                  </p>
                </>
              ) : (
                <div className="px-4 py-3 bg-slate-50 rounded-xl text-slate-700 font-medium font-mono">
                  {user.payout_phone ? maskPhone(user.payout_phone) : '— не указан —'}
                </div>
              )}
            </div>
          </div>

        {/* Кнопки */}
        <div className="flex gap-3">
          {isEditing ? (
            <>
              <button onClick={handleSave} disabled={loading} className="btn btn-primary flex-1">
                {loading ? 'Сохранение...' : '💾 Сохранить'}
              </button>
              <button onClick={() => setIsEditing(false)} className="btn btn-outline flex-1">
                Отмена
              </button>
            </>
          ) : (
            <button onClick={() => setIsEditing(true)} className="btn btn-primary w-full">
              ✏️ Редактировать профиль
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
